import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import { END_USER_APP_HOST } from '../../config';
import { useUpdateBookingPageActivationMutation } from '../../api/account/accountApiSlice';
import { useSetupProgress } from '../../hooks/useSetupProgress';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';

interface Props {
    readOnly: boolean;
    /** The tenant's slug — the end-user app is served at `<host>/<slug>`. */
    slug: string;
    /** `tenant.config.endUserAppActive`. */
    active: boolean;
}

/**
 * Switch the end-user app on and off.
 *
 * ── Why it lives in Services, next to the usage flow ───────────────────────
 *
 * It sat on Account → Experience, one tab away from the check-in/check-out
 * flow it belongs with. Both answer the same question — *how is the end-user
 * app configured?* — and splitting them across two menu items made the pair
 * hard to find and easy to misread (PO, 2026-09-12).
 *
 * ADR-0005 had already listed "booking-page activation (slug + end-user-app
 * activation)" as a migration candidate, to be moved when next meaningfully
 * touched. This is that move; the ADR's amendment records it.
 *
 * ── Why this one and not Branding, which sat beside it ─────────────────────
 *
 * Branding (primary colour, logo, favicon) carries UNSAVED form state, and it
 * is covered by the Account page's `beforeunload` guard. The Services page has
 * no such guard, so moving Branding as-is would silently drop the warning that
 * protects a half-filled upload. This section has no unsaved state at all — the
 * toggle saves on click — so it moves without that question. Branding follows
 * once Services has the guard.
 */
const BookingPageActivationSection = ({ readOnly, slug, active }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updateBookingPage, { isLoading: saving }] = useUpdateBookingPageActivationMutation();

    /*
     * Activation is gated on the first three setup steps — a first venue, bank
     * details, a legal notice — because a live page without them takes money it
     * cannot account for. `canCreateBookings` is exactly those three.
     *
     * `useSetupProgress` reads a bare `useGetAccountQuery()`, which always
     * resolves the CALLER's own tenant (#1334, #2059). That is safe here only
     * because the value is used for one thing — enabling a button that is not
     * rendered at all unless `readOnly` is false, and `readOnly` is false only
     * when the caller owns the tenant on screen. A master viewing someone
     * else's tenant sees the state, never the button. Widening this section's
     * use of the value means resolving progress for the VIEWED tenant first.
     */
    const { canCreateBookings, assetsFailed } = useSetupProgress();

    const handleToggle = async () => {
        try {
            await updateBookingPage({ active: !active }).unwrap();
            dispatch(enqueueSuccessSnackbar({
                message: t('services.booking_page_saved'),
                autoHideDuration: 3000,
            }));
        } catch (err) {
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(err, t('services.booking_page_error'), t),
                autoHideDuration: 5000,
            }));
        }
    };

    return (
        <section id={'booking-page'} className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <h2 className={'text-lg font-semibold text-foreground mb-1'}>
                {t('services.booking_page_section')}
            </h2>
            <p className={'text-sm text-foreground/60 mb-4'}>
                {t('services.booking_page_section_desc')}
            </p>

            <div className={'flex items-center justify-between gap-3'}>
                <div>
                    <p className={'text-sm font-medium text-foreground'}>
                        {active
                            ? t('services.booking_page_active')
                            : t('services.booking_page_inactive')}
                    </p>
                    {slug && (active ? (
                        <a
                            href={`https://${END_USER_APP_HOST}/${slug}`}
                            target={'_blank'}
                            rel={'noreferrer'}
                            className={'text-sm text-primary underline'}
                        >
                            {END_USER_APP_HOST}/{slug}
                        </a>
                    ) : (
                        <p className={'text-sm text-foreground/60'}>
                            {END_USER_APP_HOST}/{slug}
                        </p>
                    ))}
                </div>
                {!readOnly && (
                    <CustomButton
                        title={active
                            ? t('services.booking_page_deactivate')
                            : t('services.booking_page_activate')}
                        onClick={handleToggle}
                        isFilled={!active}
                        // Deactivating an already-live page stays allowed: the
                        // gate is on going live, not on coming back down.
                        disabled={!slug || (!active && !canCreateBookings)}
                        loading={saving}
                    />
                )}
            </div>

            {/* Why the button is disabled, rather than a dead control. The
                setup steps are on the dashboard, and without this the operator
                has no way to know which one is missing. */}
            {!active && !canCreateBookings && (
                <p role={assetsFailed ? 'alert' : undefined} className={'mt-4 text-sm text-foreground/60'}>
                    {/* A failed object read is not a missing object: say which it is. */}
                    {t(assetsFailed ? 'dashboard.setup_progress_failed' : 'services.booking_page_locked_hint')}
                </p>
            )}

            {/* A tenant with no slug cannot have a page at all — say so instead
                of showing "<host>/" and a disabled button. */}
            {!slug && (
                <p className={'mt-4 text-sm text-foreground/60'}>
                    {t('services.booking_page_no_slug')}
                </p>
            )}
        </section>
    );
};

export default BookingPageActivationSection;
